import { useCallback, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import toast from "react-hot-toast";
import { uploadImageToGhl } from "hook/services";
import ReactCropModal from "../settings/Configurator/components/ReactCropModal/ReactCropModal";
import BrandLogosSection from "../settings/Configurator/components/BasicInfo/BrandingTabs/BrandLogosSection";
import TopbarStylingSection from "../settings/Configurator/components/BasicInfo/BrandingTabs/TopbarStylingSection";
import GhlMediaGallery from "components/GhlMediaGallery";

const FIELD_LABELS = {
  topbar_bg: "Topbar background color",
  topbar_text: "Topbar text color",
  topbar_text_hover: "Topbar text hover color",
  topbar_font: "Topbar font",
  topbar_font_size: "Topbar font size",
  logo: "Header logo",
  footer_logo: "Footer logo",
  auth_logo: "Auth logo",
  Meta_logo: "Favicon",
  logo_size: "Header logo size",
  footer_logo_size: "Footer logo size",
  auth_logo_size: "Auth logo size",
};

const labelFor = (name) => FIELD_LABELS[name] || name;

const cropAspectByType = {
  logo: 2,
  footer_logo: 2,
  auth_logo: 2,
  Meta_logo: 1,
};

function ThemeStep({ agency, selectedTheme, handelThemeData, onReady }) {
  const inputData = selectedTheme?.theme_data?.general;
  const [logo, setlogo] = useState("");
  const [Meta_logo, setMeta_logo] = useState("");
  const [Footer_logo, setFooter_logo] = useState("");
  const [Auth_logo, setAuth_logo] = useState("");
  const [logoSize, setLogoSize] = useState("medium");
  const [footerLogoSize, setFooterLogoSize] = useState("medium");
  const [authLogoSize, setAuthLogoSize] = useState("medium");
  const [previewFooterLogo, setPreviewFooterLogo] = useState("");
  const [previewAuthLogo, setPreviewAuthLogo] = useState("");
  const [previewLogo, setPreviewLogo] = useState("");
  const [previewMetaLogo, setPreviewMetaLogo] = useState("");
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentImageType, setCurrentImageType] = useState("");
  const [ghlGalleryOpen, setGhlGalleryOpen] = useState(false);
  const [ghlImageType, setGhlImageType] = useState("");
  const [ghlImageIdType, setGhlImageIdType] = useState("");

  useEffect(() => {
    onReady?.(Boolean(selectedTheme?.theme_id));
  }, [selectedTheme?.theme_id, onReady]);

  useEffect(() => {
    if (!selectedTheme?.theme_id) return;
    const general = selectedTheme?.theme_data?.general || {};
    setlogo(general.logo || "");
    setMeta_logo(general.Meta_logo || "");
    setFooter_logo(general.footer_logo || "");
    setAuth_logo(general.auth_logo || "");
    setLogoSize(general.logo_size || "medium");
    setFooterLogoSize(general.footer_logo_size || "medium");
    setAuthLogoSize(general.auth_logo_size || "medium");
    setPreviewLogo(general.logo || "");
    setPreviewMetaLogo(general.Meta_logo || "");
    setPreviewFooterLogo(general.footer_logo || "");
    setPreviewAuthLogo(general.auth_logo || "");
  }, [selectedTheme?.theme_id]);

  const runAutoSave = useCallback(
    (data, { loadingMsg = "Saving...", successMsg = "Saved", errorMsg } = {}) => {
      const promise = handelThemeData(data).then((result) => {
        if (result && result.success === false) {
          throw new Error(result.message || errorMsg || "Failed to save");
        }
        return result;
      });

      toast.promise(
        promise,
        {
          loading: loadingMsg,
          success: successMsg,
          error: (err) => err?.message || errorMsg || "Failed to save",
        },
        { id: "onboarding-theme-autosave" }
      );

      return promise;
    },
    [handelThemeData]
  );

  const autoSaveField = useCallback(
    (name, value) => {
      if (!name) return;
      if (!selectedTheme?.theme_id) {
        toast.error("Theme is not configured for this agency.");
        return;
      }
      const currentValue = selectedTheme?.theme_data?.general?.[name] ?? "";
      if (currentValue === value) return;
      const label = labelFor(name);
      runAutoSave(
        { general: { [name]: value } },
        {
          loadingMsg: `Updating ${label}...`,
          successMsg: `${label} updated`,
          errorMsg: `Failed to update ${label}`,
        }
      ).catch(() => {});
    },
    [selectedTheme?.theme_id, selectedTheme?.theme_data?.general, runAutoSave]
  );

  const handleLogoSizeChange = useCallback(
    (fieldName, value) => {
      if (!selectedTheme?.theme_id) {
        toast.error("Theme is not configured for this agency.");
        return;
      }
      if (fieldName === "logo_size") setLogoSize(value);
      else if (fieldName === "footer_logo_size") setFooterLogoSize(value);
      else if (fieldName === "auth_logo_size") setAuthLogoSize(value);
      const label = labelFor(fieldName);
      runAutoSave(
        { general: { [fieldName]: value } },
        {
          loadingMsg: `Updating ${label}...`,
          successMsg: `${label} updated`,
          errorMsg: `Failed to update ${label}`,
        }
      ).catch(() => {});
    },
    [selectedTheme?.theme_id, runAutoSave]
  );

  const setUploadedUrlForType = (type, url) => {
    if (type === "logo") {
      setlogo(url);
      setPreviewLogo(url);
    } else if (type === "footer_logo") {
      setFooter_logo(url);
      setPreviewFooterLogo(url);
    } else if (type === "auth_logo") {
      setAuth_logo(url);
      setPreviewAuthLogo(url);
    } else if (type === "Meta_logo") {
      setMeta_logo(url);
      setPreviewMetaLogo(url);
    }
  };

  const openCropModal = (file, type) => {
    setCurrentImage(URL.createObjectURL(file));
    setCurrentImageType(type);
    setCropModalOpen(true);
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      openCropModal(file, type);
      e.target.value = "";
    }
  };

  const handleCroppedImage = async (blob) => {
    if (!blob) return;

    try {
      const isPng = blob.type === "image/png";
      const file = new File([blob], isPng ? "cropped-image.png" : "cropped-image.jpg", {
        type: blob.type,
      });
      const imageResponse = await uploadImageToGhl(file, agency);
      if (!imageResponse?.data) {
        toast.error("Failed to upload image", { id: "onboarding-theme-autosave" });
        return;
      }

      const uploadedUrl = imageResponse.data;
      setUploadedUrlForType(currentImageType, uploadedUrl);
      if (currentImageType === "logo") setPreviewLogo(URL.createObjectURL(blob));
      if (currentImageType === "footer_logo") setPreviewFooterLogo(URL.createObjectURL(blob));
      if (currentImageType === "auth_logo") setPreviewAuthLogo(URL.createObjectURL(blob));
      if (currentImageType === "Meta_logo") setPreviewMetaLogo(URL.createObjectURL(blob));

      if (selectedTheme?.theme_id && currentImageType) {
        const label = labelFor(currentImageType);
        runAutoSave(
          { general: { [currentImageType]: uploadedUrl } },
          {
            loadingMsg: `Uploading ${label}...`,
            successMsg: `${label} uploaded`,
            errorMsg: `Failed to upload ${label}`,
          }
        ).catch(() => {});
      }
      setCropModalOpen(false);
    } catch (error) {
      console.error("Error processing cropped image:", error);
      toast.error("Failed to upload image", { id: "onboarding-theme-autosave" });
    }
  };

  const handleRemoveImage = (fieldName, clearStates) => {
    clearStates();
    if (!selectedTheme?.theme_id) return;
    const label = labelFor(fieldName);
    runAutoSave(
      { general: { [fieldName]: "" } },
      {
        loadingMsg: `Removing ${label}...`,
        successMsg: `${label} removed`,
        errorMsg: `Failed to remove ${label}`,
      }
    ).catch(() => {});
  };

  const openGhlGallery = (type, idType) => {
    setGhlImageType(type);
    setGhlImageIdType(idType);
    setGhlGalleryOpen(true);
  };

  const handleGhlImageSelect = (pick) => {
    if (!pick?.url || !ghlImageType) return;
    setUploadedUrlForType(ghlImageType, pick.url);
    if (!selectedTheme?.theme_id) return;
    const label = labelFor(ghlImageType);
    runAutoSave(
      { general: { [ghlImageType]: pick.url, [ghlImageIdType]: pick?._id } },
      {
        loadingMsg: `Uploading ${label}...`,
        successMsg: `${label} uploaded`,
        errorMsg: `Failed to upload ${label}`,
      }
    ).catch(() => {});
  };

  if (!selectedTheme?.theme_id) {
    return <p className="sd-onboarding__note">Theme is not configured for this agency yet.</p>;
  }

  return (
    <div className="sd-onboarding__theme">
      <Grid container spacing={3}>
        <BrandLogosSection
          logo={logo}
          previewLogo={previewLogo}
          Auth_logo={Auth_logo}
          previewAuthLogo={previewAuthLogo}
          Footer_logo={Footer_logo}
          previewFooterLogo={previewFooterLogo}
          Meta_logo={Meta_logo}
          previewMetaLogo={previewMetaLogo}
          handleImageUpload={handleImageUpload}
          onGhlPick={openGhlGallery}
          inputData={inputData}
          logoSize={logoSize}
          footerLogoSize={footerLogoSize}
          authLogoSize={authLogoSize}
          onLogoSizeChange={handleLogoSizeChange}
          onRemoveLogo={() =>
            handleRemoveImage("logo", () => {
              setlogo("");
              setPreviewLogo("");
            })
          }
          onRemoveAuthLogo={() =>
            handleRemoveImage("auth_logo", () => {
              setAuth_logo("");
              setPreviewAuthLogo("");
            })
          }
          onRemoveFooterLogo={() =>
            handleRemoveImage("footer_logo", () => {
              setFooter_logo("");
              setPreviewFooterLogo("");
            })
          }
          onRemoveMetaLogo={() =>
            handleRemoveImage("Meta_logo", () => {
              setMeta_logo("");
              setPreviewMetaLogo("");
            })
          }
        />
        <TopbarStylingSection inputData={inputData} autoSaveField={autoSaveField} />
      </Grid>

      <ReactCropModal
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        currentImage={currentImage}
        onCropComplete={handleCroppedImage}
        aspectRatio={cropAspectByType[currentImageType]}
        preferPng={["logo", "footer_logo", "auth_logo", "Meta_logo"].includes(currentImageType)}
      />
      <GhlMediaGallery
        open={ghlGalleryOpen}
        onClose={() => setGhlGalleryOpen(false)}
        onSelect={handleGhlImageSelect}
        title={`Select ${labelFor(ghlImageType) || "Image"}`}
        enableCrop
        cropAspectRatio={cropAspectByType[ghlImageType] ?? null}
      />
    </div>
  );
}

export default ThemeStep;
